import React, { useLayoutEffect, useRef ,useMemo} from 'react';
import Highcharts from 'highcharts';
import HighchartsReact, {
	HighchartsReactRefObject,
} from 'highcharts-react-official';

interface DataPoint {
	x: number;
	y: number;
}

interface AlarmsUptimeTrendProps {
	title?: string;
	chargerAlarmData?: IChargerAlarmData[];
	chargerUptimeData?: IChargerUptimeData[];
}

const AlarmsUptimeTrendChart: React.FC<AlarmsUptimeTrendProps> = ({
	title = 'Alarms & Uptime Trend',
	chargerAlarmData,
	chargerUptimeData,
}) => {
	const chartRef = React.useRef<HighchartsReactRefObject>(null);
	
	const chartWrapRef = useRef<HTMLDivElement>(null);
	const chartSizeRef = useRef<{ width: number; height: number }>({
		width: 0,
		height: 200,
	});
	const titleWrapRef = useRef<HTMLDivElement>(null);
	

	// New alarm data
	const alarmData = chargerAlarmData || [];

	const len = alarmData.length;

	// New uptime data
	const uptimeDataArray = chargerUptimeData || [];

	const defaultCriticalData: DataPoint[] = alarmData.map((item, index) => ({
		x: index,
		y: Number(item.critical),
	}));

	const defaultMajorData: DataPoint[] = alarmData.map((item, index) => ({
		x: index,
		y: Number(item.major),
	}));

	const defaultMinorData: DataPoint[] = alarmData.map((item, index) => ({
		x: index,
		y: Number(item.minor),
	}));

	const finalCriticalData = defaultCriticalData;
	const finalMajorData = defaultMajorData;
	const finalMinorData = defaultMinorData;

	const successData = uptimeDataArray.map((item, index) => ({
		x: index,
		y: Number(item.avgUptimePercentage),
	}));

	const totalCritical = alarmData.reduce(
		(sum, item) => sum + Number(item.critical || 0),
		0,
	);

	const totalMajor = alarmData.reduce(
		(sum, item) => sum + Number(item.major || 0),
		0,
	);

	const totalMinor = alarmData.reduce(
		(sum, item) => sum + Number(item.minor || 0),
		0,
	);

	const options: Highcharts.Options = useMemo(() => ({
		chart: {
			type: 'spline',
			backgroundColor: 'transparent',
			// height: 265,
			spacingLeft: 15,
			spacingRight: 3,

			//spacingBottom:10
		},

		// title: {
		// 	text: title,
		// 	align: 'left',
		// 	style: {
		// 		fontSize: '18px',
		// 		fontWeight: '600',
		// 		color: '#2c3e50',
		// 	},
		// },
		title: {
			// useHTML: true,
			widthAdjust: -30,

			style: {
				fontWeight: 'normal',
			},
			align: 'left',
			// 		text: `
			// <div style="
			// 	display:flex;
			// 	justify-content:space-between;
			// 	align-items:center;
			// 	min-width: ${chartSize.width ? `${chartSize.width - 30}px` : '100%'};
			// 	">
			// 	<span style="
			// 		font-size:15px;
			// 		color:#666666;
			// 	">
			// 		${title}
			// 	</span>

			// 	<div style="
			// 		display:flex;
			// 		gap:16px;
			// 		font-size:13px;

			// 	">
			// 		<span style="display:inline-flex;align-items:center;gap:6px;">
			// 			<span style="width:10px;height:10px;border-radius:50%;background:#BB0808;"></span>
			// 			Critical : ${totalCritical}
			// 		</span>

			// 		<span style="display:inline-flex;align-items:center;gap:6px;">
			// 			<span style="width:10px;height:10px;border-radius:50%;background:#F0690F;"></span>
			// 			Major : ${totalMajor}
			// 		</span>

			// 		<span style="display:inline-flex;align-items:center;gap:6px;">
			// 			<span style="width:10px;height:10px;border-radius:50%;background:#F0BB0F;"></span>
			// 			Minor : ${totalMinor}
			// 		</span>
			// 	</div>
			// </div>
			// `,
			text: '',
		},

		credits: { enabled: false },
		exporting: { enabled: false },

		xAxis: {
			type: 'category',
			categories: alarmData.map((item) => item.date),
			labels: {
				style: {
					color: '#7f8c8d',
					fontSize: '11px',
				},
				step: Math.ceil(len / 10),
			},
			lineColor: '#e0e0e0',
			tickColor: '#e0e0e0',
			gridLineWidth: 0,
			gridLineColor: '#f5f5f5',
			plotBands: alarmData.map((_, index) => {
				const barWidthFraction = 0.25;
				return {
					from: index - barWidthFraction,
					to: index + barWidthFraction,
					color: '#f5f5f5',
				};
			}),
		},

		yAxis: [
			{
				title: {
					text: 'Alarm Volume',
					x: -4,
				},
				labels: {
					style: {
						color: '#7f8c8d',
						fontSize: '12px',
					},
				},
				gridLineColor: '#f5f5f5',
			},
			{
				title: {
					text: 'Uptime (%)',
					x: -4,
					style: {
						color: 'rgb(0, 188, 212)',
					},
				},
				labels: {
					style: {
						color: '#7f8c8d',
						fontSize: '11px',
					},
					formatter: function () {
						return Number(this.value).toFixed(2);
					},
				},
				opposite: true,
				gridLineWidth: 0,

				gridLineColor: '#e8e8e8',
			},
		],

		legend: {
			enabled: false,
		},

		plotOptions: {
			spline: {
				lineWidth: 3,
				marker: {
					enabled: false,
					states: {
						hover: {
							enabled: true,
							radius: 5,
							lineWidth: 0,
						},
					},
				},
				states: {
					hover: {
						lineWidthPlus: 1,
					},
				},
			},
		},

		tooltip: {
			shared: true,
			useHTML: true,
			borderWidth: 0,
			borderRadius: 8,
			backgroundColor: 'white',
			shadow: {
				color: 'rgba(0,0,0,0.15)',
				offsetX: 0,
				offsetY: 2,
				opacity: 0.2,
				width: 8,
			},
			padding: 12,
			style: {
				fontSize: '12px',
				zIndex: 99999,
			},
			className: 'highcharts-tooltip-override',
			// positioner: function (labelWidth, labelHeight, point) {
			// 	return {
			// 		x: point.plotX,
			// 		y: point.plotY,
			// 	};
			// },
			formatter: function () {
				const date = this.x;
				let tooltipHTML = '';

				this.points?.forEach((point) => {
					const value = point.y?.toFixed(0);
					if (point.series.name == 'Uptime') {
						tooltipHTML += `
            <div style="margin-bottom:4px;display:flex;align-items:center;gap:6px;">
			  <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background-color:${point.color};"></span>
			  <div>
           <b>${point.series.name}:</b> ${value}%
		   </div>
            </div>`;
					} else {
						tooltipHTML += `
            <div style="margin-bottom:4px;display:flex;align-items:center;gap:6px;">
			 <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background-color:${point.color};"></span>
              <span><b>${point.series.name}:</b> ${value}</span>
            </div>
          `;
					}
				});
				tooltipHTML += ` <div style="font-weight:500;margin-top:4px; color:#7C8D8F">${date}</div>`;
				return tooltipHTML;
			},
		},

		series: [
			{
				type: 'spline',
				name: 'Critical',
				data: finalCriticalData,
				color: '#BB0808',
				lineWidth: 2,
				zIndex: 3,
				marker: {
					enabled: false,
					symbol: 'circle',
				},
			},
			{
				type: 'spline',
				name: 'Major',
				data: finalMajorData,
				color: '#F0690F',
				lineWidth: 2,
				zIndex: 2,
				marker: {
					enabled: false,
					symbol: 'circle',
				},
			},
			{
				type: 'spline',
				name: 'Minor',
				data: finalMinorData,
				color: '#F0BB0F',
				lineWidth: 2,
				zIndex: 1,
				marker: {
					enabled: false,
					symbol: 'circle',
				},
			},
			{
				type: 'column',
				name: 'Uptime',
				yAxis: 1,
				data: successData,
				color: '#06BFBF',
				borderRadius: 8,
				pointPadding: 0.2,
				groupPadding: 0.1,
			},
		],
	}),[alarmData,uptimeDataArray]);



// useLayoutEffect(() => {

// 	if (!chartWrapRef.current) return;

// 	const observer = new ResizeObserver(() => {
// 		const wrapRect = chartWrapRef.current!.getBoundingClientRect();
// 		const titleRect = titleWrapRef.current?.getBoundingClientRect();

// 		const titleHeight = titleRect?.height ?? 0;

// 		setChartSize({
// 			width: wrapRect.width - 5,
// 			height: wrapRect.height - titleHeight,
// 		});
// 	});

// 	observer.observe(chartWrapRef.current);
// 	return () => observer.disconnect();
// }, []);

	useLayoutEffect(() => {
	if (!chartWrapRef.current) return;

	let rafId: number | null = null;

	const observer = new ResizeObserver(entries => {
		const { width, height } = entries[0].contentRect;

		const newWidth = Math.floor(width);
		const newHeight = Math.floor(height);
		const titleRect = titleWrapRef.current?.getBoundingClientRect();
		const titleHeight = titleRect?.height ?? 0;
		const prev = chartSizeRef.current;
		if (prev.width === newWidth && prev.height === newHeight) return;

		chartSizeRef.current = {
			width: newWidth,
			height: newHeight-titleHeight-2,
		};

		// Break ResizeObserver → layout loop
		if (rafId) cancelAnimationFrame(rafId);

		rafId = requestAnimationFrame(() => {
			const chart = chartRef.current?.chart;
			if (chart) {
				chart.setSize(newWidth, newHeight-titleHeight-2, false);
			}
		});
	});

	observer.observe(chartWrapRef.current);

	return () => {
		if (rafId) cancelAnimationFrame(rafId);
		observer.disconnect();
	};
}, []);

	return (
		<div className='alarmuptime-trend-chart' ref={chartWrapRef}>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					padding: '0 15px 10px 15px',
					fontSize: '13px',
				}}
				ref={titleWrapRef}
			>
				<span style={{ fontSize: '15px', color: '#666666' }}>{title}</span>
				<div style={{ display: 'flex', gap: '16px' }}>
					<span
						style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
					>
						<span
							style={{
								width: '10px',
								height: '10px',
								borderRadius: '50%',
								background: '#BB0808',
							}}
						></span>
						Critical : {totalCritical}
					</span>
					<span
						style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
					>
						<span
							style={{
								width: '10px',
								height: '10px',
								borderRadius: '50%',
								background: '#F0690F',
							}}
						></span>
						Major : {totalMajor}
					</span>
					<span
						style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
					>
						<span
							style={{
								width: '10px',
								height: '10px',
								borderRadius: '50%',
								background: '#F0BB0F',
							}}
						></span>
						Minor : {totalMinor}
					</span>
				</div>
			</div>
			
				<HighchartsReact
					highcharts={Highcharts}
					options={options}
					immutable={false}
					allowChartUpdate={true}
					updateArgs={[true, true, true]}
					ref={chartRef}
				/>
		</div>
	);
};

export default AlarmsUptimeTrendChart;
